import { normalizeExternalUrl } from './normalizeExternalUrl';

export interface FormattedLinkItem {
  label: string;
  href: string;
  displayUrl: string;
}

const KNOWN_PLATFORMS: Array<{ pattern: RegExp; name: string }> = [
  { pattern: /(?:^|\.)linkedin\.com/i, name: 'LinkedIn' },
  { pattern: /(?:^|\.)(?:youtube\.com|youtu\.be)/i, name: 'YouTube' },
  { pattern: /(?:^|\.)(?:twitter\.com|x\.com)/i, name: 'X (Twitter)' },
  { pattern: /(?:^|\.)instagram\.com/i, name: 'Instagram' },
  { pattern: /(?:^|\.)(?:facebook\.com|fb\.me)/i, name: 'Facebook' },
  { pattern: /(?:^|\.)tiktok\.com/i, name: 'TikTok' },
  { pattern: /(?:^|\.)github\.com/i, name: 'GitHub' },
  { pattern: /(?:^|\.)medium\.com/i, name: 'Medium' },
  { pattern: /(?:^|\.)substack\.com/i, name: 'Substack' },
  { pattern: /(?:^|\.)techcrunch\.com/i, name: 'TechCrunch' },
  { pattern: /(?:^|\.)forbes\.com/i, name: 'Forbes' },
  { pattern: /(?:^|\.)bloomberg\.com/i, name: 'Bloomberg' },
  { pattern: /(?:^|\.)reuters\.com/i, name: 'Reuters' },
  { pattern: /(?:^|\.)crunchbase\.com/i, name: 'Crunchbase' },
  { pattern: /(?:^|\.)pitchbook\.com/i, name: 'PitchBook' },
  { pattern: /(?:^|\.)producthunt\.com/i, name: 'Product Hunt' },
  { pattern: /(?:^|\.)discord\.(?:gg|com)/i, name: 'Discord' },
  { pattern: /(?:^|\.)slack\.com/i, name: 'Slack' },
  { pattern: /(?:^|\.)reddit\.com/i, name: 'Reddit' },
  { pattern: /(?:^|\.)spotify\.com/i, name: 'Spotify' },
  { pattern: /(?:^|\.)apple\.com\/.*podcast/i, name: 'Apple Podcasts' },
  { pattern: /(?:^|\.)wired\.com/i, name: 'Wired' },
  { pattern: /(?:^|\.)theverge\.com/i, name: 'The Verge' },
  { pattern: /(?:^|\.)businessinsider\.com/i, name: 'Business Insider' },
  { pattern: /(?:^|\.)wsj\.com/i, name: 'WSJ' },
  { pattern: /(?:^|\.)ft\.com/i, name: 'Financial Times' },
  { pattern: /(?:^|\.)nytimes\.com/i, name: 'NY Times' },
  { pattern: /(?:^|\.)bbc\.(?:com|co\.uk)/i, name: 'BBC' },
  { pattern: /(?:^|\.)cnbc\.com/i, name: 'CNBC' },
  { pattern: /(?:^|\.)venturebeat\.com/i, name: 'VentureBeat' },
];

/**
 * Detect the platform / site label for a given URL (e.g. LinkedIn, YouTube, TechCrunch).
 * If not recognized, derives a clean label from the hostname.
 */
export function getLinkPlatformLabel(url: string): string {
  if (!url) return 'Link';
  const trimmed = url.trim();

  let hostname = '';
  try {
    const normalized = normalizeExternalUrl(trimmed);
    const parsed = new URL(normalized);
    hostname = parsed.hostname.toLowerCase().replace(/^www\./i, '');
  } catch {
    hostname = trimmed.toLowerCase();
  }

  // Try matching known platform patterns against hostname or full trimmed URL
  for (const { pattern, name } of KNOWN_PLATFORMS) {
    if (pattern.test(hostname) || pattern.test(trimmed)) {
      return name;
    }
  }

  // Fallback to domain name from URL
  if (hostname) {
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const main = parts[parts.length - 2];
      if (main && main.length > 0) {
        return main.charAt(0).toUpperCase() + main.slice(1);
      }
    }
    return hostname.charAt(0).toUpperCase() + hostname.slice(1);
  }

  return 'Link';
}

/**
 * Combines and formats social media and other media/press links into a deduplicated list
 * with detected platform labels.
 */
export function formatLinksAndMedia(
  socialUrls: string[] | string | undefined | null,
  otherUrls: string[] | string | undefined | null
): FormattedLinkItem[] {
  const parseList = (raw: string[] | string | undefined | null): string[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      return raw.split(',').map(s => s.trim());
    }
    return [];
  };

  const rawList = [...parseList(socialUrls), ...parseList(otherUrls)];
  const seenHrefs = new Set<string>();
  const results: FormattedLinkItem[] = [];

  for (const item of rawList) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (!trimmed) continue;

    const href = normalizeExternalUrl(trimmed);
    if (!href) continue;

    const lowerHref = href.toLowerCase();
    if (seenHrefs.has(lowerHref)) continue;
    seenHrefs.add(lowerHref);

    const label = getLinkPlatformLabel(trimmed);
    results.push({
      label,
      href,
      displayUrl: trimmed,
    });
  }

  return results;
}
